import { Box, Stack } from '@mui/material';
import { useAuth } from '@schooly/components/authentication';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import { useMarkbookFilterContext } from '../../../../context/markbook/MarkbookFilterContext';
import useAppLocation from '../../../../hooks/useAppLocation';
import { AccessDenied } from '@schooly/style';
import { useMarkbookContextNavigation } from '../MarkbookEditor/helpers';
import { MarkbookHeaderLoading } from '../MarkbookHeader';
import {
  MarkbookSettingsLoading,
  MarkbookViewSettings,
} from '../MarkbookSettings/MarkbookSettings';
import { canViewMarkbook } from '../permissions';
import { MARKBOOK_SETTINGS_STORAGE_KEY, MarkbookPreviewContent } from './MarkbookPreviewContent';
import { MarkbookPreviewSkeletonTable } from './MarkbookPreviewSkeletonTable';

export const MarkbookPreview: FC<PropsWithChildren> = ({ children }) => {
  const location = useAppLocation();
  const { permissions } = useAuth();
  const { filterPreview, filter, filterId } = useMarkbookFilterContext();
  const initialSettings = useMemo(() => {
    const settings = localStorage.getItem(MARKBOOK_SETTINGS_STORAGE_KEY);
    return settings ? (JSON.parse(settings) as MarkbookViewSettings) : null;
  }, []);

  const {
    handleChangeFilterData,
    handleEditFilter,
    handleCreateNewMarkbookFilters,
    handleNavigateToMainPage,
  } = useMarkbookContextNavigation();

  const isPreviewRoute = location.pathname === '/markbook/filters/preview';
  const hasNoFilter = !filterId && !filterPreview;
  const canShowFilter = Boolean(filterPreview || (filterId && filter));

  useEffect(() => {
    if (hasNoFilter && isPreviewRoute) {
      handleNavigateToMainPage();
    }
  }, [hasNoFilter, handleNavigateToMainPage, isPreviewRoute]);

  const handleEditMarkbook = useCallback(() => {
    if (filter) {
      handleEditFilter(filter);
    } else {
      handleChangeFilterData();
    }
  }, [filter, handleChangeFilterData, handleEditFilter]);

  const hasAccess = canViewMarkbook(permissions);

  if (!hasAccess) {
    return <AccessDenied />;
  }

  if (!filter && !filterPreview) {
    return (
      <MarkbookPreviewSkeleton>
        <MarkbookPreviewSkeletonTable />
        {children}
      </MarkbookPreviewSkeleton>
    );
  }

  return (
    <MarkbookPreviewContent
      filter={filter}
      filterPreview={filterPreview}
      canShowFilter={canShowFilter}
      initialSettings={initialSettings}
      onMarkbookCreate={handleCreateNewMarkbookFilters}
      onMarkbookEdit={handleEditMarkbook}
    >
      {children}
    </MarkbookPreviewContent>
  );
};

export const MarkbookPreviewSkeleton: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Stack overflow="hidden" height="100%" position="relative" gap={1}>
      <MarkbookHeaderLoading />
      <MarkbookSettingsLoading />
      <Box
        sx={{
          position: 'absolute',
          top: '90px',
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {children}
      </Box>
    </Stack>
  );
};
